import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Typography } from '@mui/material';
import AppLoader from '../components/AppLoader';
import SessionBar from '../components/SessionBar';
import * as Colors from '../theme/colors';
import {
  SessionSecurityControl,
  fetchSessionPolicy,
  streamProtocols,
  streamUrl,
} from '../services/contractors';
import SessionWatermark from '../components/SessionWatermark';

const ContractorDesktop: React.FC = () => {
  const { isAuthenticated, isLoading, user, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    void loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    });
  }, [isLoading, isAuthenticated, loginWithRedirect]);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const organization = params.get('organization') ?? '';
  const resource = params.get('resource') ?? '';
  const port = Number(params.get('port') ?? '3389');

  const frame = useRef<HTMLIFrameElement>(null);
  const [status, setStatus] = useState('Credential required');
  const [viewerReady, setViewerReady] = useState(false);
  const [live, setLive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [policy, setPolicy] = useState<SessionSecurityControl | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'zerac-viewer-ready') setViewerReady(true);
      if (event.data?.type === 'zerac-connecting') setConnecting(true);
      if (event.data?.type === 'zerac-cancelled') navigate('/portal');
      if (event.data?.type === 'zerac-connected') {
        setLive(true);
        setConnectedAt(Date.now());
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate]);

  useEffect(() => {
    if (connectedAt === null) return;
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - connectedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [connectedAt]);

  useEffect(() => {
    if (!isAuthenticated || !organization) return;
    let cancelled = false;
    getAccessTokenSilently()
      .then((token) => fetchSessionPolicy(token, organization))
      .then((resolved) => {
        if (!cancelled) setPolicy(resolved);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, organization, getAccessTokenSilently]);

  const start = useCallback(async () => {
    if (!viewerReady || !frame.current?.contentWindow || !policy) return;
    try {
      const token = await getAccessTokenSilently();
      frame.current.contentWindow.postMessage(
        {
          type: 'zerac-stream',
          url: streamUrl(organization, resource, port),
          protocols: streamProtocols(token),
          clipboardAccess: policy.clipboardAccess,
          fileTransferEnabled: policy.enableFileTransfer,
        },
        window.location.origin
      );
      setStatus('Credential required');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [viewerReady, getAccessTokenSilently, organization, resource, port, policy]);

  useEffect(() => {
    if (viewerReady && policy) void start();
  }, [viewerReady, policy, start]);

  if (isLoading || !isAuthenticated) return <AppLoader />;

  const minutes = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const seconds = String(elapsed % 60).padStart(2, '0');

  return (
    <Box sx={{ height: '100vh', bgcolor: Colors.Black, display: 'flex', flexDirection: 'column' }}>
      <SessionBar
        label="Remote Desktop"
        online={Boolean(connectedAt)}
        status={status}
        elapsed={connectedAt ? `${minutes}:${seconds}` : undefined}
        onEnd={() => (navigate('/portal'))}
        showFullscreen
        recording={policy?.enableSessionRecording}
      >
        {live && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const next = !muted;
              setMuted(next);
              frame.current?.contentWindow?.postMessage(
                { type: 'zerac-set-muted', muted: next },
                window.location.origin
              );
            }}
          >
            {muted ? 'Unmute' : 'Mute'}
          </Button>
        )}
      </SessionBar>

      {error && (
        <Typography sx={{ color: Colors.Red, fontSize: 13, px: 4, py: 1.5 }}>{error}</Typography>
      )}

      {connecting && !live && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mb: 4,
              borderRadius: '50%',
              border: `4px solid ${Colors.Selected}33`,
              borderTopColor: Colors.Selected,
              animation: 'zspin 0.9s linear infinite',
              '@keyframes zspin': { to: { transform: 'rotate(360deg)' } },
            }}
          />
          <Typography sx={{ color: Colors.White, fontSize: 20, mb: 1 }}>
            Connecting to remote desktop...
          </Typography>
          <Typography sx={{ color: Colors.LightGray, fontSize: 12 }}>Powered by Zerac</Typography>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: 0,
          display: connecting && !live ? 'none' : 'block',
        }}
      >
        <Box
          component="iframe"
          ref={frame}
          src="/desktop-viewer.html"
          title="Remote desktop"
          sx={{ position: 'absolute', inset: 0, border: 'none', width: '100%', height: '100%' }}
        />
        {policy && live && (
          <SessionWatermark
            watermarking={policy.watermarking}
            identity={user?.email || user?.name || ''}
            startedAt={connectedAt}
          />
        )}
      </Box>
    </Box>
  );
};

export default ContractorDesktop;
